import { observer } from "mobx-react-lite";
import { Circle, CircleCheck } from "lucide-react";
import { useTran } from "@/hooks";
import { PillTabs } from "@/components/common";
import { useDisclosureStore } from "../../hooks/useDataEntryStore";
import { authSessionStore } from "@/features/auth/stores/auth-session.store";
import { hasParentAccess } from "@/features/esg/utils/org-scope.utils";

const CategoryTabs = observer(() => {
  const { t } = useTran();
  const {
    activeCategory,
    setActiveCategory,
    disclosuresMatchingNonCategoryFilters,
    disclosurePresence,
    presenceFilter,
    setPresenceFilter,
  } = useDisclosureStore();

  const showGeneral = hasParentAccess(authSessionStore.user);

  const tabs = [
    { value: "All Metrics", label: t("All Metrics") },
    ...(showGeneral ? [{ value: "General", label: t("General") }] : []),
    { value: "Environmental", label: t("Environmental") },
    { value: "Social", label: t("Social") },
    { value: "Governance", label: t("Governance") },
  ].map((tab) => ({
    ...tab,
    count:
      tab.value === "All Metrics"
        ? disclosuresMatchingNonCategoryFilters.length
        : disclosuresMatchingNonCategoryFilters.filter(
            (d) => d.category === tab.value,
          ).length,
  }));

  const categoryDisclosures =
    activeCategory === "All Metrics"
      ? disclosuresMatchingNonCategoryFilters
      : disclosuresMatchingNonCategoryFilters.filter(
          (d) => d.category === activeCategory,
        );
  const withData = categoryDisclosures.filter((d) =>
    disclosurePresence.has(d._id),
  ).length;
  const total = categoryDisclosures.length;
  const withoutData = total - withData;

  return (
    <div className="flex items-center gap-3">
      <PillTabs
        tabs={tabs}
        value={activeCategory}
        onChange={setActiveCategory}
      />
      {total > 0 && (
        <div
          className="flex items-center gap-2 text-xs text-muted-foreground"
          title={t("Disclosures with data vs empty in this category")}
        >
          <button
            type="button"
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors ${
              presenceFilter === "filled"
                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                : "text-muted-foreground"
            }`}
            onClick={() => setPresenceFilter("filled")}
          >
            {presenceFilter === "filled" ? (
              <CircleCheck
                size={12}
                className="text-emerald-500 dark:text-emerald-400"
              />
            ) : (
              <Circle size={12} className="text-muted-foreground/40" />
            )}
            <span className="tabular-nums">{withData}</span>
            <span>{t("Filled")}</span>
          </button>
          <button
            type="button"
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors ${
              presenceFilter === "empty"
                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                : "text-muted-foreground"
            }`}
            onClick={() => setPresenceFilter("empty")}
          >
            {presenceFilter === "empty" ? (
              <CircleCheck
                size={12}
                className="text-emerald-500 dark:text-emerald-400"
              />
            ) : (
              <Circle size={12} className="text-muted-foreground/40" />
            )}
            <span className="tabular-nums">{withoutData}</span>
            <span>{t("Empty")}</span>
          </button>
        </div>
      )}
    </div>
  );
});

export default CategoryTabs;
